'use client';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Pagination, PaginationItem } from '@mui/material';

export interface NewsPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const NewsPagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: NewsPaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<unknown>, page: number) => {
    onPageChange(page);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 8,
        mb: 4,
      }}
    >
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handleChange}
        renderItem={item => (
          <PaginationItem
            components={{
              previous: ChevronLeft,
              next: ChevronRight,
            }}
            {...item}
            sx={{
              width: 40,
              height: 40,
              margin: 0,
              borderRadius: '20px',
              border: 'none',
              fontSize: '16px',
              fontFamily:
                'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: item.selected ? 700 : 400,
              color: item.selected ? '#00388F' : '#666666',
              backgroundColor: '#FFFFFF',
              minWidth: 40,

              '&:hover': {
                backgroundColor: item.selected
                  ? 'rgba(0, 56, 143, 0.04)'
                  : 'rgba(0, 0, 0, 0.04)',
              },

              // Selected state - blue text with orange underline
              '&.Mui-selected': {
                backgroundColor: '#FFFFFF',
                color: '#00388F',
                fontWeight: 700,
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(0, 56, 143, 0.04)',
                },
                // Orange underline indicator exactly like Figma
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#EB722E',
                  borderRadius: '0px',
                },
              },

              // Previous/Next arrow buttons
              '&.MuiPaginationItem-previousNext': {
                backgroundColor: '#FFFFFF',
                color: '#666666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-disabled': {
                  opacity: 0.38,
                  color: '#666666',
                  backgroundColor: '#FFFFFF',
                },
              },

              // Ellipsis styling
              '&.MuiPaginationItem-ellipsis': {
                fontSize: '16px',
                color: '#666666',
                backgroundColor: 'transparent',
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 400,
                lineHeight: 1.5,
                textAlign: 'center',
              },
            }}
          />
        )}
        sx={{
          '& .MuiPagination-ul': {
            flexWrap: 'wrap',
            gap: '6px',
            padding: '0 6px',
          },
        }}
      />
    </Box>
  );
};

export default NewsPagination;
